import type { JSX, ReactNode } from 'react';
import { isWaitingStatus, waitElapsedClock } from '../lib/hooks.ts';
import { AgentLogo, Icon } from '../lib/icons.tsx';
import {
  AGENTS,
  agentForModel,
  coerceEffort,
  effortLabel,
  effortsFor,
  modelLabel,
  resolvedModel,
  type PrefAxis,
} from '../lib/models.ts';
import { sessionHasTask, SKIP_VERIFICATION_NOTE, type Session } from '../lib/protocol.ts';

export function SessionContents({
  session,
  now,
  onOpen,
  onRun,
  onStop,
  onRotate,
  onSkipVerification,
  onParallelTask,
  onRestore,
}: {
  session: Session;
  now: number;
  onOpen?: () => void;
  onRun?: () => void;
  onStop?: () => void;
  onRotate?: (axis: PrefAxis) => void;
  onSkipVerification?: (skip: boolean) => void;
  onParallelTask?: (parallel: boolean) => void;
  /** Replaces run/stop/done. Used on the backlog to send a chat back to Home. */
  onRestore?: () => void;
}): JSX.Element {
  const interactive = onOpen != null;
  const hasTask = sessionHasTask(session);
  const taskDone = hasTask && session.taskDone;
  // `paused` is a run waiting out the Claude 5-hour limit. It counts as busy:
  // the wait clock is still running and Stop still has something to stop.
  const paused = session.runStatus === 'paused';
  const busy = isWaitingStatus(session.runStatus);
  const agent = agentForModel(resolvedModel(session.model));
  const agentRow = AGENTS.find((a) => a.id === agent)!;
  const family = resolvedModel(session.model);
  const fullModelLabel = modelLabel(family);
  const compactModelLabel = compactSessionModelLabel(fullModelLabel);
  const rung = coerceEffort(family, session.effort);
  const canRotate = onRotate != null;
  const titleRow = (
    <div className="session-top">
      <span className="session-title">{session.title}</span>
    </div>
  );
  const meta = (
    <div className="session-meta">
      <PrefTag
        className="tag-agent"
        label={`Provider: ${agentRow.label}. Tap to switch.`}
        canRotate={canRotate}
        onRotate={() => onRotate?.('agent')}
      >
        <AgentLogo agent={agent} size={10} />
      </PrefTag>
      <PrefTag
        label={`Model: ${fullModelLabel}. Tap to switch.`}
        canRotate={canRotate}
        onRotate={() => onRotate?.('model')}
      >
        {compactModelLabel}
      </PrefTag>
      {rung && effortsFor(family).length > 0 && (
        <PrefTag
          label={`Effort: ${effortLabel(rung)}. Tap to switch.`}
          canRotate={canRotate}
          onRotate={() => onRotate?.('effort')}
        >
          {rung[0]!.toUpperCase()}
        </PrefTag>
      )}
      {hasTask &&
        (onSkipVerification ? (
          <button
            type="button"
            className={`tag session-tag tag-skip-verify${session.skipVerification ? ' tag-skip-verify-on' : ''}`}
            onClick={() => onSkipVerification(!session.skipVerification)}
            aria-label="Skip verification"
            aria-pressed={session.skipVerification}
            title={SKIP_VERIFICATION_NOTE}
          >
            <Icon name="flaskOff" size={10} strokeWidth={2.4} />
          </button>
        ) : session.skipVerification ? (
          <span
            className="tag tag-skip-verify tag-skip-verify-on"
            title={SKIP_VERIFICATION_NOTE}
            aria-label="Skip verification"
          >
            <Icon name="flaskOff" size={10} strokeWidth={2.4} />
          </span>
        ) : null)}
      {hasTask &&
        (onParallelTask ? (
          <button
            type="button"
            className={`tag session-tag tag-parallel${session.parallelTask ? ' tag-parallel-on' : ''}`}
            onClick={() => onParallelTask(!session.parallelTask)}
            aria-label="Run alongside other tasks in this project"
            aria-pressed={session.parallelTask}
            title="Run alongside other tasks in this project. Tasks share a working tree, so auto-commits may include another task's edits."
          >
            <Icon name="parallel" size={12} strokeWidth={2.4} />
          </button>
        ) : session.parallelTask ? (
          <span className="tag tag-parallel tag-parallel-on" title="Runs alongside other tasks in this project">
            <Icon name="parallel" size={12} strokeWidth={2.4} />
          </span>
        ) : null)}
      {session.runStatus === 'error' && <span className="tag tag-error">failed</span>}
    </div>
  );
  const main = (
    <>
      {interactive ? (
        <button type="button" className="session-open" onClick={onOpen}>
          {titleRow}
        </button>
      ) : (
        titleRow
      )}
      {meta}
    </>
  );
  let run: ReactNode = null;
  if (onRestore) {
    run = (
      <button
        type="button"
        className="session-run"
        onClick={onRestore}
        aria-label={`Move to home: ${session.title}`}
      >
        <Icon name="home" size={18} strokeWidth={2.4} />
      </button>
    );
  } else if (busy) {
    const running = session.runStatus === 'running';
    const icon = running ? 'pause' : 'stop';
    const kind = running ? 'session-run-pause' : 'session-run-stop';
    const glyph = <Icon name={icon} size={18} strokeWidth={2.4} />;
    run = interactive ? (
      <button
        type="button"
        className={`session-run ${kind}`}
        onClick={onStop}
        aria-label={
          session.runStatus === 'queued'
            ? `Remove from queue: ${session.title}`
            : paused
              ? `Drop this turn instead of waiting for the usage limit: ${session.title}`
              : `Pause agent: ${session.title}`
        }
      >
        {glyph}
      </button>
    ) : (
      <span className={`session-run ${kind}`} aria-hidden="true">
        {glyph}
      </span>
    );
  } else if (hasTask && taskDone) {
    run = (
      <span
        className="session-run session-run-done"
        aria-label={interactive ? `Task done: ${session.title}` : undefined}
        aria-hidden={interactive ? undefined : true}
      >
        <Icon name="check" size={18} strokeWidth={2.4} />
      </span>
    );
  } else if (hasTask) {
    run = interactive ? (
      <button
        type="button"
        className="session-run"
        onClick={onRun}
        aria-label={`Run task: ${session.title}`}
      >
        <Icon name="play" size={18} strokeWidth={2.4} />
      </button>
    ) : (
      <span className="session-run" aria-hidden="true">
        <Icon name="play" size={18} strokeWidth={2.4} />
      </span>
    );
  }
  const timeLabel =
    session.lastSubmittedAt != null
      ? waitElapsedClock(session.lastSubmittedAt, session.lastEndedAt, busy, now)
      : null;
  return (
    <>
      <div className="session-main">{main}</div>
      {(run || timeLabel) && (
        <div className="session-side">
          {run}
          {timeLabel && (
            <span
              className="session-time elapsed"
              title={busy ? 'Time since last prompt' : 'Last wait'}
            >
              {timeLabel}
            </span>
          )}
        </div>
      )}
    </>
  );
}

function compactSessionModelLabel(label: string): string {
  const name = label.match(/\b(Opus|Sol|Astra|Luna|Composer|Grok)\b/i)?.[1];
  return name ? name[0]!.toUpperCase() + name.slice(1).toLowerCase() : label;
}

function PrefTag({
  className,
  label,
  canRotate,
  onRotate,
  children,
}: {
  className?: string;
  label: string;
  canRotate: boolean;
  onRotate: () => void;
  children: ReactNode;
}): JSX.Element {
  const cls = `tag session-tag${className ? ` ${className}` : ''}`;
  if (!canRotate) {
    return (
      <span className={cls} title={label}>
        {children}
      </span>
    );
  }
  return (
    <button type="button" className={cls} title={label} aria-label={label} onClick={onRotate}>
      {children}
    </button>
  );
}
